import { DomainSchema } from '@multitec/domain'
import { useQuery } from 'react-query'
import { z } from 'zod'

import { Base } from '../base'
import { MyURLSearchParams } from '../utils/url-search-params'

class Create extends Base {
  private body = z.object({
    name: z.string(),
    type: z.enum(['EQUIPAMENTO', 'BATERIA']),
    manufacturer: z.string().optional(),
    imageStr: z.instanceof(File).optional(),
  })

  private response = z.object({
    id: z.string(),
  })

  handle = async (body: z.infer<typeof this.body>) => {
    const formData = new FormData()
    formData.append('name', body.name)
    formData.append('type', body.type)
    body.manufacturer && formData.append('manufacturer', body.manufacturer)
    body.imageStr && formData.append('imageStr', body.imageStr)

    const { data } = await this.axios.post('/entity/equipments/', formData)
    return this.response.parse(data.result)
  }
}

class Edit extends Base {
  private body = z.object({
    name: z.string(),
    type: z.enum(['EQUIPAMENTO', 'BATERIA']),
    manufacturer: z.string().optional(),
    imageStr: z.instanceof(File).optional(),
    active: z.boolean(),
  })

  private response = z.object({
    id: z.string(),
  })

  handle = async (id: string, body: z.infer<typeof this.body>) => {
    const formData = new FormData()
    formData.append('name', body.name)
    formData.append('type', body.type)
    body.manufacturer && formData.append('manufacturer', body.manufacturer)
    body.imageStr && formData.append('imageStr', body.imageStr)

    const { data } = await this.axios.put('/entity/equipments/' + id, formData)
    return this.response.parse(data.result)
  }
}

class Search extends Base {
  private searchParams = z.object({
    search: z.string().optional(),
    type: z.enum(['EQUIPAMENTO', 'BATERIA']).optional(),
    active: z.boolean().optional(),
  })

  private response = z.array(DomainSchema.Equipment)

  handle = (searchParams: z.infer<typeof this.searchParams>) => {
    return useQuery(['equipment', 'search', { ...searchParams }], async () => {
      const queryParams = new MyURLSearchParams(searchParams).toString()
      const { data } = await this.axios.get('/entity/equipments?' + queryParams)
      return this.response.parse(data.result)
    })
  }
}

class LoadOne extends Base {
  private response = DomainSchema.Equipment

  handle = (id: string) => {
    return useQuery(['equipment', 'load-one', { id }], async () => {
      const { data } = await this.axios.get('/entity/equipments/' + id)
      return this.response.parse(data.result)
    })
  }
}

export default class extends Base {
  Create = new Create(this.axios).handle
  Edit = new Edit(this.axios).handle
  Search = new Search(this.axios).handle
  LoadOne = new LoadOne(this.axios).handle
}
